import React, { useCallback, useMemo, useState } from "react";
import { ClipboardList, FileText, Eye, Download, Sparkles } from "lucide-react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { useInspections } from "../../../hooks/useInspections";
import { inspectionsAPI } from "../../../services/api";
import InspectionReportModal from "../project/inspections/components/report/InspectionReportModal";
import WeeklyReportsView from "./WeeklyReportsView";

const DetailedInspectionReportsView = () => {
  const { activeProject } = useProjectContext();
  const projectId = activeProject?.id;

  const { inspections, loading, error, reload } = useInspections();

  const [reportModal, setReportModal] = useState({
    open: false,
    inspectionId: null,
    inspectionNumber: null,
  });

  const [downloadingId, setDownloadingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);

  const openReportModal = useCallback((inspection) => {
    if (!inspection) return;
    setReportModal({
      open: true,
      inspectionId: inspection.id,
      inspectionNumber: inspection.inspection_number || null,
    });
  }, []);

  const closeReportModal = useCallback(() => {
    setReportModal((prev) => ({ ...prev, open: false }));
  }, []);

  const handleViewPdf = useCallback(
    async (inspection) => {
      if (!projectId || !inspection?.id) return;
      setViewingId(inspection.id);
      try {
        const res = await inspectionsAPI.downloadReportPdf(
          projectId,
          inspection.id,
        );
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        console.error("Failed to view PDF:", e);
        alert(e?.response?.data?.detail || "Failed to open report PDF");
      } finally {
        setViewingId(null);
      }
    },
    [projectId],
  );

  const handleDownloadPdf = useCallback(
    async (inspection) => {
      if (!projectId || !inspection?.id) return;
      setDownloadingId(inspection.id);
      try {
        const res = await inspectionsAPI.downloadReportPdf(
          projectId,
          inspection.id,
        );
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        const n = inspection.inspection_number || "inspection";
        a.href = url;
        a.download = `detailed_inspection_report_${n}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error("Failed to download PDF:", e);
        alert(e?.response?.data?.detail || "Failed to download report PDF");
      } finally {
        setDownloadingId(null);
      }
    },
    [projectId],
  );

  const sortedInspections = [...(inspections || [])].sort((a, b) => {
    const da = a.inspection_date || "";
    const db = b.inspection_date || "";
    return db.localeCompare(da);
  });

  if (!activeProject) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Detailed Inspection Report
          </h2>
          <p className="text-gray-600 text-sm">
            View and download detailed PDF reports for individual approved
            inspections.
          </p>
        </div>
        <ClipboardList className="w-8 h-8 text-blue-600" />
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-medium text-gray-800">
            Detailed Reports Archive
          </div>
          <button
            onClick={reload}
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>

        {loading && (
          <div className="text-sm text-gray-500">
            Loading detailed inspection reports...
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600">
            Failed to load inspections for reports.
          </div>
        )}

        {!loading && !error && sortedInspections.length === 0 && (
          <div className="text-sm text-gray-500">
            No inspections yet. Create inspections in the Inspections tab to
            generate reports.
          </div>
        )}

        {!loading && !error && sortedInspections.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Inspection number
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Date
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Type
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Site location
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Status
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedInspections.map((ins) => {
                  const n = ins.inspection_number || "-";
                  const status = (ins.status || "").toLowerCase();
                  const statusColor =
                    status === "approved"
                      ? "bg-green-100 text-green-800"
                      : status === "submitted"
                        ? "bg-yellow-100 text-yellow-800"
                        : status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-700";

                  const isReportAvailable = status === "approved";
                  const isDownloading = downloadingId === ins.id;
                  const isViewing = viewingId === ins.id;

                  return (
                    <tr key={ins.id} className="border-b last:border-0">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <span className="font-medium text-gray-900">{n}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {ins.inspection_date || "-"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {ins.inspection_type || "-"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {ins.site_location || ins.location}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs capitalize ${statusColor}`}
                        >
                          {status || "draft"}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openReportModal(ins)}
                            disabled={!isReportAvailable}
                            className="px-2 py-1.5 rounded-md border text-xs hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            title={
                              isReportAvailable
                                ? "Open report setup"
                                : "Report is available after inspection approval"
                            }
                          >
                            Open report
                          </button>

                          <button
                            type="button"
                            onClick={() => handleViewPdf(ins)}
                            disabled={isViewing || !isReportAvailable}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border text-xs hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                            title={
                              isReportAvailable
                                ? "Open PDF in new tab"
                                : "Report is available after inspection approval"
                            }
                          >
                            <Eye className="w-4 h-4" />
                            {isViewing ? "Opening…" : "View"}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDownloadPdf(ins)}
                            disabled={isDownloading || !isReportAvailable}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                            title={
                              isReportAvailable
                                ? "Download PDF"
                                : "Report is available after inspection approval"
                            }
                          >
                            <Download className="w-4 h-4" />
                            {isDownloading ? "Downloading…" : "Download"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <InspectionReportModal
        open={reportModal.open}
        onClose={closeReportModal}
        projectId={projectId}
        inspectionId={reportModal.inspectionId}
        inspectionNumber={reportModal.inspectionNumber}
      />
    </div>
  );
};

const REPORT_SECTIONS = [
  {
    id: "detailed",
    label: "Detailed Inspection Reports",
    description:
      "Open, configure, view, and download reports for individual approved inspections.",
    icon: ClipboardList,
  },
  {
    id: "generator",
    label: "Inspection Report Generator",
    description:
      "Create compact period-based reports from inspections and issue activity.",
    icon: FileText,
  },
];

const DailyReportsView = () => {
  const { activeProject, projectRole, currentUser } = useProjectContext();
  const [activeReportTab, setActiveReportTab] = useState("detailed");

  const canUseGenerator = useMemo(() => {
    if ((currentUser?.role || "").toLowerCase() === "admin") return true;
    return ["project_manager", "qa_qc_manager", "inspector"].includes(
      (projectRole || "").toLowerCase(),
    );
  }, [currentUser?.role, projectRole]);

  const visibleSections = useMemo(
    () =>
      REPORT_SECTIONS.filter(
        (section) => section.id !== "generator" || canUseGenerator,
      ),
    [canUseGenerator],
  );

  if (!activeProject) return null;

  const safeActiveTab = visibleSections.some(
    (section) => section.id === activeReportTab,
  )
    ? activeReportTab
    : "detailed";

  return (
    <div className="space-y-5">
      <section className="overflow-hidden rounded-2xl border border-orange-100 bg-gradient-to-r from-orange-50 via-white to-amber-50 shadow-sm">
        <div className="flex flex-col gap-4 px-5 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-orange-500 p-2.5 text-white shadow-sm">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Reports</h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-600">
                Individual detailed inspection reports and reporting-period
                summaries in one workspace.
              </p>
            </div>
          </div>

          <div className="text-xs font-medium text-gray-500">
            Project: <span className="text-gray-800">{activeProject.name}</span>
          </div>
        </div>
      </section>

      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-2 shadow-sm">
        <div
          className={`grid gap-2 ${visibleSections.length > 1 ? "md:grid-cols-2" : "grid-cols-1"}`}
        >
          {visibleSections.map((section) => {
            const Icon = section.icon;
            const active = safeActiveTab === section.id;

            return (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveReportTab(section.id)}
                className={`group flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition-all ${
                  active
                    ? "border-orange-300 bg-white shadow-sm ring-1 ring-orange-100"
                    : "border-transparent bg-transparent hover:border-gray-200 hover:bg-white"
                }`}
              >
                <span
                  className={`mt-0.5 rounded-lg p-2 transition ${
                    active
                      ? "bg-orange-500 text-white"
                      : "bg-white text-gray-500 group-hover:text-orange-600"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span
                    className={`block text-sm font-semibold ${
                      active ? "text-orange-700" : "text-gray-800"
                    }`}
                  >
                    {section.label}
                  </span>
                  <span className="mt-0.5 block text-xs leading-5 text-gray-500">
                    {section.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div key={safeActiveTab}>
        {safeActiveTab === "generator" ? (
          <WeeklyReportsView />
        ) : (
          <DetailedInspectionReportsView />
        )}
      </div>
    </div>
  );
};

export default DailyReportsView;
